import React, { useState, useRef, useEffect, useCallback } from "react";

/**
 * Glassmorphism dropdown / select component.
 *
 * Props:
 *  - value        current value
 *  - onChange(v)   called with the selected option's value
 *  - options       [{ value, label, icon? }]
 *  - placeholder   text when nothing is selected
 *  - label         optional label above the dropdown
 *  - disabled
 *  - className     extra classes on the outer wrapper
 */
export default function Dropdown({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  label,
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find((o) => o.value === value);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && (
        <span className="block text-xs font-label font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider">
          {label}
        </span>
      )}

      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-body transition-colors duration-200 border
          ${
            open
              ? "bg-surface-container-high border-primary/30 ring-1 ring-primary/20"
              : "bg-surface-container border-outline-variant/15 hover:border-outline-variant/30"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          text-on-surface`}
      >
        <span className={`truncate ${!selected ? "text-on-surface-variant/60" : ""}`}>
          {selected ? (
            <span className="flex items-center gap-2">
              {selected.icon && (
                <span className="material-symbols-outlined text-base">{selected.icon}</span>
              )}
              {selected.label}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <span
          className={`material-symbols-outlined text-lg text-on-surface-variant transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          expand_more
        </span>
      </button>

      {/* Options popover */}
      {open && (
        <div className="absolute z-50 mt-1.5 w-full glass-card-elevated rounded-xl border border-outline-variant/15 py-1 max-h-60 overflow-y-auto">
          {options.map((opt) => {
            const isActive = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  close();
                }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors
                  ${isActive ? "text-primary bg-primary/5" : "text-on-surface hover:bg-on-surface/[.06]"}`}
              >
                {opt.icon && (
                  <span className="material-symbols-outlined text-base">{opt.icon}</span>
                )}
                <span className="truncate">{opt.label}</span>
                {isActive && (
                  <span className="material-symbols-outlined text-base ml-auto">check</span>
                )}
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="px-4 py-3 text-sm text-on-surface-variant/60">No options</p>
          )}
        </div>
      )}
    </div>
  );
}
